import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useAppContext } from 'context/AppContext';
import { makeStyles } from 'styles/util';
import strings from 'localization/strings';
import { formatDateTime } from 'helpers/DateHelper';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { DatePicker as MuiDatePicker } from '@material-ui/pickers';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';

const useStyles = makeStyles(({ theme, colors }) => ({
    nullableDatePicker: {
        // zero padding to enable header background
        '& .MuiPickersModal-dialogRoot': {
            padding: 0
        },
        // margin for button container at the bottom of the picker dialog
        '& .MuiDialogActions-root': {
            margin: theme.spacing(0, 1, 1, 1)
        },
        // hack to make the clear button stand out
        '& .MuiPickersModal-withAdditionalAction button:first-child': {
            color: colors.white,
            backgroundColor: colors.primaryColor,
            '&:hover': {
                backgroundColor: colors.primaryHoverColor
            }
        }
    }
}));

const NullableDatePickerDialog = ({ open, title, description, initialValue, onOk, onCancel, label, formatFunc, size, inputVariant, helperText, nullString, className, fieldProps = {}, nullButtonLabel = undefined, disabled, ...rest }) => {
    const classes = useStyles();
    const { appContext } = useAppContext();
    const [ datePickerOpen, setDatePickerOpen ] = useState(false);
    const [ value, setValue ] = useState(initialValue);

    const handleOk = () => {
        onOk(value);
    };

    const handleDateChange = newValue => {
        setValue(newValue);
    };

    return (
        <Dialog maxWidth="md" open={open} onClose={onCancel}>
            <DialogTitle disableTypography>
                <Typography variant="h5">
                    {title}
                </Typography>
            </DialogTitle>
            <DialogContent>
                {
                    description &&
                    (
                        <Typography variant="body1" gutterBottom>
                            {description}
                        </Typography>
                    )
                }
                <MuiDatePicker
                    fullWidth={true}
                    autoOk={true}
                    open={datePickerOpen}
                    onOpen={() => setDatePickerOpen(true)}
                    onClose={() => setDatePickerOpen(false)}
                    onChange={handleDateChange}
                    label={label}
                    value={value}
                    format={formatFunc(appContext)}
                    size={size}
                    inputVariant={inputVariant}
                    helperText={helperText}
                    className={className}
                    InputProps={{ ...fieldProps }}
                    DialogProps={{ className: classes.nullableDatePicker}}
                    disabled={disabled}
                    clearable
                    okLabel={strings.ok}
                    cancelLabel={strings.cancel}
                    clearLabel={nullButtonLabel ?? nullString}
                    labelFunc={date => date ? formatDateTime(date, formatFunc, appContext) : nullString}
                    {...rest}
                />
            </DialogContent>
            <DialogActions>
                <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    onClick={handleOk}
                >
                    {strings.ok}
                </Button>
                <Button
                    type="button"
                    variant="outlined"
                    color="primary"
                    onClick={onCancel}
                >
                    {strings.cancel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

NullableDatePickerDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    initialValue: PropTypes.instanceOf(Date),
    onOk: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    label: PropTypes.string,
    formatFunc: PropTypes.func.isRequired,
    size: PropTypes.string,
    inputVariant: PropTypes.string,
    helperText: PropTypes.string,
    nullString: PropTypes.string.isRequired,
    className: PropTypes.string,
    fieldProps: PropTypes.object,
    nullButtonLabel: PropTypes.string,
    disabled: PropTypes.bool
};

export default NullableDatePickerDialog;
